import { z } from 'zod';
import {
    levelCustomCopySchema,
    levelNoteSchema,
    levelSchema,
    levelUpdateSchema,
} from '../types/levels';
import { nullableDefaultObject } from '..';
import { submissionSchema } from '../types/submissions';
import { baseUserSchema } from '../types/users';

const partialLevelSchema = nullableDefaultObject(levelSchema);

export const levelFormSchema = partialLevelSchema
    .pick({
        name: true,
        description: true,
        position: true,
        level_id: true,
        publisher: true,
        song: true,
        tags: true,
        status: true,
        two_player: true,
        requires_raw_footage: true,
    })
    .transform((level) => ({
        ...level,
        id: undefined,
        publisher: undefined,
        publisher_id: level.publisher?.id,
    }));

export type levelFormInput = z.input<typeof levelFormSchema>;
export type levelFormOutput = z.output<typeof levelFormSchema>;

export const levelEditFormSchema = z.object({
    list: z.enum(['classic', 'platformer']),
    level: levelFormSchema,
    creators: z
        .array(baseUserSchema)
        .transform((creators) => creators.map((creator) => creator.id) || []),
});

export type levelEditFormInput = z.input<typeof levelEditFormSchema>;
export type levelEditFormOutput = z.output<typeof levelEditFormSchema>;

export const levelMoveFormSchema = levelEditFormSchema
    .pick({
        list: true,
    })
    .extend({
        level: partialLevelSchema.pick({
            status: true,
            position: true,
        }),
    });

export type levelMoveFormInput = z.input<typeof levelMoveFormSchema>;
export type levelMoveFormOutput = z.output<typeof levelMoveFormSchema>;

export const levelPlaceFormSchema = levelEditFormSchema.extend({
    verification: nullableDefaultObject(
        submissionSchema.pick({
            submitted_by: true,
            completion_time: true,
            video_url: true,
            mobile: true,
        }),
    ).transform((submission) => ({
        ...submission,
        submitted_by: submission.submitted_by?.id,
        is_verification: true,
    })),
});

export type levelPlaceFormInput = z.input<typeof levelPlaceFormSchema>;
export type levelPlaceFormOutput = z.output<typeof levelPlaceFormSchema>;

export const partialLevelCustomCopyFormSchema = nullableDefaultObject(
    levelCustomCopySchema,
);

export const levelCustomCopyFormSchema = partialLevelCustomCopyFormSchema
    .pick({
        status: true,
        copy_id: true,
        id_type: true,
        description: true,
    })
    .transform((custom_copy) => ({
        ...custom_copy,
        id: undefined,
    }));

export type levelCustomCopyFormInput = z.input<
    typeof levelCustomCopyFormSchema
>;
export type levelCustomCopyFormOutput = z.output<
    typeof levelCustomCopyFormSchema
>;

export const partialLevelNoteFormSchema =
    nullableDefaultObject(levelNoteSchema);

export const levelNoteFormSchema = partialLevelNoteFormSchema
    .pick({
        note_type: true,
        note: true,
        timestamp: true,
    })
    .transform((note) => ({
        ...note,
        id: undefined,
    }));

export type levelNoteFormInput = z.input<typeof levelNoteFormSchema>;
export type levelNoteFormOutput = z.output<typeof levelNoteFormSchema>;

export const partialLevelUpdateFormSchema =
    nullableDefaultObject(levelUpdateSchema);

export const levelUpdateFormSchema = partialLevelUpdateFormSchema
    .pick({
        changelog: true,
        level_id: true,
        update_type: true,
        timestamp: true,
    })
    .transform((update) => ({
        ...update,
        id: undefined,
    }));

export type levelUpdateFormInput = z.input<typeof levelUpdateFormSchema>;
export type levelUpdateFormOutput = z.output<typeof levelUpdateFormSchema>;
